import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {MetaData} from '@models/adminstration/meta-data';
import {Observable, Subject} from 'rxjs';
import {ModalService} from '@services/modal/modal.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {ConfirmationDialogComponent} from '@shared/confirmation-dialog/confirmation-dialog.component';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {ELASTIC_SEARCH_TYPE} from '@constants/elastic-search/elastic-search-type.enum';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {Machine} from '@models/machine/machine';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {MetadataEditComponent} from '@components/administration/metadata/modals/metadata-edit.component';
import {UtilsService} from '@services/utils/utils.service';

@Component({
  selector: 'scitis-admin-metadata-action',
  templateUrl: './metadata-action.component.html',
  styleUrl: './metadata-action.component.scss',
  standalone: false
})
export class MetadataActionComponent implements OnDestroy {
  @Input()
  metadata: MetaData | any;

  @Input()
  parent: Gateway | Machine | null = null;

  @Input()
  type: ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA | null = null;

  @Input()
  userIsAllowedToEdit: boolean = false;

  @Output()
  metadataDeleteEvent = new EventEmitter<MetaData>();

  @Output()
  metadataSaveEvent = new EventEmitter<MetaData>();

  private destroy$: Subject<boolean> = new Subject<boolean>();

  private readonly PageName: string = 'metadata-action';


  constructor(
      private modalService: ModalService,
      private loadingIndicator: LoadingIndicatorService,
      private notificationService: NotificationService,
      private translationService: TranslateService,
      private gatewayService: GatewayService,
      private machineService: MachineService,
      private utilsService: UtilsService
  ) {

  }

  protected onDeleteMetadata(metaDataItem: MetaData) {

    console.log(`${this.type}. removing Metadata : ${metaDataItem.key}`);

    // open modal
    const removeMessageTitle = this.translationService.instant('Delete MetaData');
    const removeMessageBody = this.translationService.instant('Do you really want to permanently delete metaData %key ?').replace('%key', metaDataItem.key?.toBold());
    const removeMessageOK = this.translationService.instant('Delete');
    const modalRef = this.modalService.open(ConfirmationDialogComponent);
    modalRef.componentInstance.title = removeMessageTitle;
    modalRef.componentInstance.body = removeMessageBody;
    modalRef.componentInstance.cancelButtonText = '';
    modalRef.componentInstance.okButtonText = removeMessageOK;

    modalRef.result.then(() => {
    }, (reason) => {
      if (reason === ModalDialogDismissOption.CANCEL || reason === ModalDialogDismissOption.CLOSE) {
        return;
      }
      if (this.parent === null || !this.parent.id || !this.type || !metaDataItem || !metaDataItem.key) {
        return;
      }

      let deleteObservable: Observable<any> = new Observable<any>();
      if (this.type === ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA) {
        deleteObservable = this.gatewayService.deleteMetaData(String(this.parent.id), metaDataItem);
      } else if (this.type === ELASTIC_SEARCH_TYPE.MACHINE_META_DATA) {
        deleteObservable = this.machineService.deleteMetaData(String(this.parent.id), metaDataItem);
      }
      this.loadingIndicator.show(this.translationService.instant('Deleting MetaData'));

      deleteObservable.subscribe(
          {
            next: (success) => {
              if (success) {
                this.metadataDeleteEvent.emit(this.metadata);
                const message = this.translationService.instant('MetaData key %key deleted.').replace('%key', metaDataItem.key?.toBold());
                this.notificationService.success(message, this.PageName, false);
                this.loadingIndicator.hide();
              } else {
                const message = this.translationService.instant('MetaData key %key could not be deleted!').replace('%key', metaDataItem.key?.toBold());
                this.notificationService.warning(message, this.PageName);
                this.loadingIndicator.hide();
              }
            }, error: (error) => {
              const message = this.translationService.instant('Error deleting MetaData key %key !').replace('%key', metaDataItem.key?.toBold());
              this.notificationService.error(message, error, this.PageName, true);
              this.loadingIndicator.hide();
            }
          });
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  protected onEditMetadata(metadata: MetaData) {
    if (!metadata || !metadata.id) {
      return;
    }

    const backup = this.utilsService.getObjectDeepCopy(metadata);

    const modalRef = this.modalService.open(MetadataEditComponent, {size: 'md'});
    modalRef.componentInstance.metadata = metadata;
    modalRef.componentInstance.parentId = this.parent?.id;
    modalRef.componentInstance.parentName = this.parent?.name;
    modalRef.componentInstance.type = this.type;
    modalRef.componentInstance.isNewMetaData = false;
    modalRef.result.then((result: MetaData) => {

      if (this.parent === null || !this.parent.id || !this.type) {
        return;
      }
      this.loadingIndicator.show();
      let saveObservable: Observable<any> = new Observable<any>();
      if (this.type === ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA) {
        saveObservable = this.gatewayService.saveMetaData(String(this.parent.id), result);
      } else if (this.type === ELASTIC_SEARCH_TYPE.MACHINE_META_DATA) {
        saveObservable = this.machineService.saveMetaData(String(this.parent.id), result);
      }

      saveObservable.subscribe({
        next: (success: MetaData) => {
          this.metadataSaveEvent.emit(success);
          this.loadingIndicator.hide();
        }, error: (error) => {
          this.loadingIndicator.hide();
          const message = this.translationService.instant('Error saving metadata key %key!').replace('%key', result.key?.toBold());
          this.notificationService.error(message, error, this.PageName, true);
          metadata = backup;
        }
      });
    });
  }
}
